import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import authService from '../services/auth.service';
import {
    usePresenceHeartbeat,
    usePresences,
    presenceEtat,
} from '../utils/presence';
import '../styles/TeamPresence.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ETAT_LABELS = {
    actif: 'En ligne',
    inactif: 'Inactif',
    hors_ligne: 'Hors ligne',
};

const initials = (prenom, nom) =>
    `${(prenom || '').charAt(0)}${(nom || '').charAt(0)}`.toUpperCase() || '?';

/** Texte d'activité : « Modifie le projet Dupont » / « Consulte le Planning » */
function activityText(p) {
    if (!p?.activiteLabel) return null;
    return p.enEdition ? `Modifie ${p.activiteLabel}` : `Consulte ${p.activiteLabel}`;
}

/**
 * Bloc « Équipe » de la sidebar : qui est en ligne dans mon entité principale,
 * façon Discord — pastille d'état + activité en cours sous chaque nom.
 * En « Mode admin » (interrupteur sous le profil, grade Superadmin uniquement),
 * le panneau montre toutes les entités avec un tag d'entité par personne.
 * Héberge aussi la boucle de heartbeat de présence (montée une seule fois).
 */
const TeamPresence = ({ adminMode = false }) => {
    usePresenceHeartbeat();

    const me = authService.getCurrentUser();
    const presences = usePresences();
    const [members, setMembers] = useState([]);       // tous les utilisateurs actifs
    const [myEntiteId, setMyEntiteId] = useState(null);
    // Permission presence_toutes_entites (DevTool → grade), fallback Superadmin
    const scope = adminMode && authService.peutPresenceToutes() ? 'tous' : 'equipe';
    const [open, setOpen] = useState(false);
    // Tick pour faire « vieillir » les états (hors ligne après 3 min sans signal)
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const iv = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        if (!me?.id) return;

        axios.get(`${API}/users`, {
            withCredentials: true,
            headers: { Accept: 'application/ld+json' },
            params: { pagination: false },
        }).then((res) => {
            const users = res.data['hydra:member'] || res.data.member || [];
            const self = users.find((u) => Number(u.id) === Number(me.id));
            setMyEntiteId(self?.entityReference?.id ?? null);
            setMembers(
                users
                    .filter((u) => u.actif !== false)
                    .map((u) => ({
                        id: Number(u.id),
                        prenom: u.prenom,
                        nom: u.nom,
                        entiteId: u.entityReference?.id ?? null,
                        entiteNom: u.entityReference?.nom ?? null,
                    })),
            );
        }).catch(() => {});
    }, [me?.id]);

    const presenceByUser = useMemo(() => {
        const map = {};
        presences.forEach((p) => { map[p.utilisateurId] = p; });
        return map;
    }, [presences]);

    const visibleMembers = useMemo(() => (
        scope === 'tous'
            ? members
            : members.filter((m) => myEntiteId != null && Number(m.entiteId) === Number(myEntiteId))
    ), [members, scope, myEntiteId]);

    const rows = useMemo(() => {
        const enriched = visibleMembers.map((m) => {
            const p = presenceByUser[m.id];
            return { ...m, presence: p, etat: presenceEtat(p, now), isMe: Number(m.id) === Number(me?.id) };
        });
        const rank = { actif: 0, inactif: 1, hors_ligne: 2 };
        enriched.sort((a, b) => (rank[a.etat] - rank[b.etat])
            || (a.entiteNom || '').localeCompare(b.entiteNom || '')
            || (a.prenom || '').localeCompare(b.prenom || ''));
        return enriched;
    }, [visibleMembers, presenceByUser, now, me?.id]);

    const online = rows.filter((r) => r.etat !== 'hors_ligne');

    if (!me?.id || visibleMembers.length === 0) return null;

    return (
        <div className="tp-block">
            <button
                type="button"
                className="tp-toggle"
                onClick={() => setOpen((o) => !o)}
                title="Voir l'équipe en ligne"
            >
                <span className="tp-toggle-icon"><Users size={16} strokeWidth={2.2} /></span>
                <span className="tp-toggle-label">Équipe</span>
                <span className="tp-avatars">
                    {online.slice(0, 4).map((r) => (
                        <span key={r.id} className={`tp-mini-avatar tp-mini--${r.etat}`}>
                            {initials(r.prenom, r.nom)}
                        </span>
                    ))}
                </span>
                <span className={`tp-count${online.length > 0 ? ' tp-count--on' : ''}`}>
                    {online.length}
                </span>
            </button>

            {open && (
                <>
                    <div className="tp-overlay" onClick={() => setOpen(false)} />
                    <div className="tp-panel" role="dialog" aria-label="Équipe en ligne">
                        <div className="tp-panel-hd">
                            <span className="tp-panel-title">
                                {scope === 'tous' ? 'Toute la société' : 'Mon équipe'}
                            </span>
                            <span className="tp-panel-sub">{online.length} en ligne · {rows.length} membre{rows.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="tp-list">
                            {rows.map((r) => (
                                <div key={r.id} className={`tp-row tp-row--${r.etat}`}>
                                    <span className={`tp-avatar tp-avatar--${r.etat}`}>
                                        {initials(r.prenom, r.nom)}
                                        <span className={`tp-dot tp-dot--${r.etat}`} />
                                    </span>
                                    <span className="tp-row-main">
                                        <span className="tp-row-name">
                                            {r.prenom} {r.nom}
                                            {r.isMe && <span className="tp-me-tag">toi</span>}
                                            {scope === 'tous' && r.entiteNom && (
                                                <span className="tp-entite-tag">{r.entiteNom}</span>
                                            )}
                                        </span>
                                        <span className="tp-row-sub">
                                            {r.etat !== 'hors_ligne' && activityText(r.presence)
                                                ? (
                                                    <span className={r.presence?.enEdition ? 'tp-row-editing' : undefined}>
                                                        {activityText(r.presence)}
                                                    </span>
                                                )
                                                : ETAT_LABELS[r.etat]}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default TeamPresence;
